import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, AlertTriangle, Loader2 } from "lucide-react";

const CONTAINER_ID = "accessi-qr-reader";

export const CameraScanDialog = ({ open, onOpenChange, onResult }) => {
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const stopScanner = async () => {
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (inst) {
        try { await inst.stop(); } catch (e) { /* già fermo */ }
        try { inst.clear(); } catch (e) { /* noop */ }
      }
    };

    const start = async () => {
      setError("");
      setStarting(true);
      try {
        const mod = await import("html5-qrcode");
        const Html5Qrcode = mod.Html5Qrcode;
        if (cancelled) return;
        const instance = new Html5Qrcode(CONTAINER_ID, { verbose: false });
        scannerRef.current = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          async (decodedText) => {
            if (cancelled || !decodedText) return;
            cancelled = true;
            await stopScanner();
            onResult((decodedText || "").trim());
            onOpenChange(false);
          },
          () => { /* frame senza codice: ignora */ }
        );
        if (cancelled) await stopScanner();
      } catch (e) {
        setError("Impossibile accedere alla fotocamera. Verifica i permessi del browser oppure usa lo scanner o la digitazione manuale.");
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    if (open) start();
    return () => { cancelled = true; stopScanner(); };
  }, [open, onOpenChange, onResult]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="accessi-camera-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5" /> Scansiona QR tesserino</DialogTitle>
        </DialogHeader>
        {error ? (
          <div className="rounded-lg bg-[color:var(--brand-red-100)] text-[color:var(--brand-red-600)] p-3 text-sm flex items-start gap-2" data-testid="accessi-camera-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            {starting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Inquadra il codice QR presente sul tesserino associativo.
          </div>
        )}
        <div id={CONTAINER_ID} className="w-full rounded-lg overflow-hidden bg-slate-100 min-h-[220px]" />
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="accessi-camera-close"><X className="h-4 w-4 mr-2" /> Chiudi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
